import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { translateKey } from 'src/locales/translate';
import {
  formatStockQuantity,
  measureShortLabel,
} from 'src/sections/@dashboard/profile/components/associatedMedicineStock';
import { useStockBatches } from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import { parseStockQuantity } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  StockHandoverChoice,
  StockPrescriptionChoice,
} from './medicationStockValues';

const PRESCRIPTION_ANSWERS: Record<StockPrescriptionChoice, string> = {
  ISSUED: 'warehouse.clinical.summaryPrescriptionIssued',
  NOT_ISSUED: 'warehouse.clinical.summaryPrescriptionNotIssued',
  '': 'warehouse.clinical.summaryPrescriptionMissing',
};

const HANDOVER_ANSWERS: Record<StockHandoverChoice, string> = {
  ADMINISTERED: 'warehouse.clinical.rowStockAdministered',
  HANDED_OVER: 'warehouse.clinical.rowStockHandedOver',
  '': 'warehouse.clinical.summaryHandoverMissing',
};

/**
 * The summary's word on stock (#692): "Z magazynu: 2 jedn., podany w
 * placówce, partia KET-1" — "automatycznie" when FEFO picks, as it always
 * does for a treatment line — or, for a saved taking the vet opted out of,
 * that the save gives it back. Nothing for a drug not taken from stock.
 * An Rx drug handed over also says its prescription answer (P7B-R2): the
 * vet may reach the summary without having seen "Magazyn". So does a
 * handover still to be given (P7B-R10), even before its quantity.
 */
export default function MedicationSummaryStock(): React.ReactElement | null {
  const { watch } = useFormContext<MedicationsFormModalValuesProps>();
  const [
    enabled,
    productId,
    quantity,
    measure,
    handover,
    batchId,
    wasSet,
    isRx,
    prescription,
  ] = watch([
    'stockEnabled',
    'stockProductId',
    'stockQuantity',
    'stockMeasure',
    'stockHandover',
    'stockBatchId',
    'stockWasSet',
    'stockProductIsRx',
    'stockPrescription',
  ]);
  const batches = useStockBatches(enabled && batchId ? productId : '');

  if (!productId) return null;
  if (!enabled) {
    return wasSet ? (
      <Typography variant="body2" data-testid="medication-summary-stock">
        {translateKey('warehouse.clinical.summaryReversal')}
      </Typography>
    ) : null;
  }
  const taken = parseStockQuantity(quantity);
  if (taken === null && handover) return null;
  const pinned = batches.find((candidate) => candidate.id === batchId);
  let batchLabel = translateKey('warehouse.clinical.summaryBatchAuto');
  if (batchId) {
    batchLabel =
      pinned?.batchNumber ??
      translateKey('warehouse.clinical.summaryBatchPinned');
  }
  const said = {
    handover: translateKey(HANDOVER_ANSWERS[handover]),
    batch: batchLabel,
  };
  const saidWith = (counted: number): string => {
    const parts = {
      quantity: formatStockQuantity(counted),
      measure: measureShortLabel(measure),
      ...said,
    };
    return handover === 'HANDED_OVER' && isRx
      ? translateKey('warehouse.clinical.summaryStockPrescription', {
          ...parts,
          prescription: translateKey(PRESCRIPTION_ANSWERS[prescription]),
        })
      : translateKey('warehouse.clinical.summaryStock', parts);
  };
  // No quantity is worked out before the handover it depends on.
  const text =
    taken === null
      ? translateKey('warehouse.clinical.summaryStockNoQuantity', said)
      : saidWith(taken);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      data-testid="medication-summary-stock"
    >
      <Typography variant="body2" color="text.secondary">
        {`${translateKey('warehouse.clinical.sectionTitle')}:`}
      </Typography>
      <Typography variant="body2">{text}</Typography>
    </Stack>
  );
}
